/**
 * OrganizationsPage — org CRUD (ORG-3). Row click → OrganizationDetailPage
 * (Members / Groups / Projects tabs).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
} from '@/store/api/organizationsApi';
import type { Organization } from '@/types';
import { format } from 'date-fns';

export function OrganizationsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });

  const { data: organizations, isLoading } = useGetOrganizationsQuery();
  const [createOrg] = useCreateOrganizationMutation();
  const [updateOrg] = useUpdateOrganizationMutation();
  const [deleteOrg, { isLoading: isDeleting }] = useDeleteOrganizationMutation();

  const orgList = (organizations as Organization[] | undefined) ?? [];

  const columns: ColumnDef<Organization, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium">{row.original.name}</p>
            {row.original.description && (
              <p className="text-xs text-muted-foreground truncate max-w-xs">{row.original.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.isActive ? 'active' : 'stopped'} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditing(null);
    setForm({ name: '', description: '' });
    setModalOpen(true);
  }

  function openEdit(org: Organization) {
    setEditing(org);
    setForm({ name: org.name, description: org.description ?? '' });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = { name: form.name.trim(), description: form.description.trim() || undefined };
    try {
      if (editing) {
        await updateOrg({ id: editing._id, body }).unwrap();
      } else {
        await createOrg(body).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save organization');
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      try {
        await deleteOrg(deleteTarget._id).unwrap();
        setDeleteTarget(null);
        toast.success('Organization deleted');
      } catch (err) {
        toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete organization');
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            Manage organizations, members, groups and projects (ORG-3)
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Organization
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground">
            Click an organization to manage its members, user groups and projects.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : orgList.length === 0 ? (
            <EmptyState
              title="No organizations"
              description="Create an organization to model your team structure"
              action={<Button onClick={openCreate}>Create Organization</Button>}
            />
          ) : (
            <DataTable
              columns={columns}
              data={orgList}
              onRowClick={(row) => navigate(`/organizations/${row._id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update organization details.' : 'Create a new organization.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Organization"
        description={`Delete "${deleteTarget?.name}"? Members, groups and project links will be affected.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}